import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { Onboarding } from '@/features/hrm/onboarding';
import {
  ONBOARDING_STATUSES,
  onboardingKindLabel,
  onboardingStatusLabel,
  onboardingStatusVariant,
} from '@/features/hrm/onboarding';
import { ONBOARDING_BASE } from '../api';
import { CabinetEmptyState, CabinetListFooter, CabinetListToolbar } from '../components/listChrome';
import { createCabinetViewStore, viewModes } from '../store/cabinetView';
import { useCabinetOnboarding, useMarkCabinetOnboardingItem } from '../queries';

const useOnboardingUi = createCabinetViewStore('cabinet.onboarding:ui', 'table', 1);
const MODES = viewModes(['table', 'list', 'cards', 'grouped']);
type OnboardingColumn = 'kind' | 'status' | 'started_on';

const progress = (ob: Onboarding) => `${ob.items.filter((i) => i.is_done).length}/${ob.items.length}`;

export function CabinetOnboardingPage() {
  const navigate = useNavigate();
  const open = (ob: Onboarding) => navigate(`/me/onboarding/${ob.public_id}`);
  const view = useOnboardingUi((s) => s.view);
  const setView = useOnboardingUi((s) => s.setView);
  const ordering = useOnboardingUi((s) => s.ordering);
  const setOrdering = useOnboardingUi((s) => s.setOrdering);
  const [page, setPage] = useState(1);

  const { data, isPending, isError, error, isFetching } = useCabinetOnboarding({ page, ordering });
  const mark = useMarkCabinetOnboardingItem();
  const rows = data?.results ?? [];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">Onboarding</h1>

      <CabinetListToolbar
        view={view}
        onViewChange={setView}
        modes={MODES}
        exportBase={ONBOARDING_BASE}
        exportParams={{ ordering }}
      />

      {isError ? (
        <p className="text-destructive text-sm">{(error as { message?: string })?.message}</p>
      ) : isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : view === 'table' ? (
        <OnboardingTable rows={rows} ordering={ordering} onOrderingChange={setOrdering} onOpen={open} />
      ) : view === 'list' ? (
        <OnboardingList rows={rows} onOpen={open} />
      ) : view === 'cards' ? (
        <OnboardingCards rows={rows} onToggle={(id, isDone) => mark.mutate({ id, isDone })} busy={mark.isPending} />
      ) : (
        <OnboardingGroupedByStatus rows={rows} onToggle={(id, isDone) => mark.mutate({ id, isDone })} busy={mark.isPending} />
      )}

      <CabinetListFooter
        count={data?.count ?? 0}
        page={page}
        onPageChange={setPage}
        hasPrev={Boolean(data?.previous)}
        hasNext={Boolean(data?.next)}
        isFetching={isFetching}
        noun={['checklist', 'checklists']}
      />
    </div>
  );
}

function OnboardingTable({
  rows,
  ordering,
  onOrderingChange,
  onOpen,
}: {
  rows: Onboarding[];
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
  onOpen: (ob: Onboarding) => void;
}) {
  const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead<OnboardingColumn> column="kind" value={ordering} onChange={onOrderingChange}>
              Kind
            </SortableTableHead>
            <SortableTableHead<OnboardingColumn> column="status" value={ordering} onChange={onOrderingChange}>
              Status
            </SortableTableHead>
            <TableHead className="text-right">Progress</TableHead>
            <SortableTableHead<OnboardingColumn>
              column="started_on"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden sm:table-cell"
            >
              Started
            </SortableTableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-muted-foreground py-10 text-center">
                You have no onboarding checklists.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((ob) => (
              <TableRow key={ob.id} className="cursor-pointer" onClick={() => onOpen(ob)}>
                <TableCell className="font-medium">{onboardingKindLabel(ob.kind)}</TableCell>
                <TableCell>
                  <Badge variant={onboardingStatusVariant(ob.status)}>
                    {onboardingStatusLabel(ob.status)}
                  </Badge>
                </TableCell>
                <TableCell className="text-right tabular-nums">{progress(ob)}</TableCell>
                <TableCell className="text-muted-foreground hidden sm:table-cell">
                  {fmtDate(ob.started_on)}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function OnboardingList({ rows, onOpen }: { rows: Onboarding[]; onOpen: (ob: Onboarding) => void }) {
  if (rows.length === 0) return <EmptyText />;
  return (
    <ul className="divide-y rounded-md border">
      {rows.map((ob) => (
        <li key={ob.id}>
          <button
            type="button"
            onClick={() => onOpen(ob)}
            className="hover:bg-accent/30 flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left text-sm"
          >
            <span className="min-w-0 flex-1 truncate font-semibold">
              {onboardingKindLabel(ob.kind)}
            </span>
            <span className="text-muted-foreground tabular-nums">{progress(ob)} done</span>
            <Badge variant={onboardingStatusVariant(ob.status)} className="shrink-0">
              {onboardingStatusLabel(ob.status)}
            </Badge>
          </button>
        </li>
      ))}
    </ul>
  );
}

interface CardsProps {
  rows: Onboarding[];
  onToggle: (id: number, isDone: boolean) => void;
  busy: boolean;
}

function OnboardingCards({ rows, onToggle, busy }: CardsProps) {
  if (rows.length === 0) return <EmptyText />;
  return (
    <div className="space-y-4">
      {rows.map((ob) => (
        <Card key={ob.id}>
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center justify-between gap-2 text-base">
              <span>{onboardingKindLabel(ob.kind)}</span>
              <span className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">{progress(ob)} done</span>
                <Badge variant={onboardingStatusVariant(ob.status)}>
                  {onboardingStatusLabel(ob.status)}
                </Badge>
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {ob.items.length === 0 ? (
              <p className="text-muted-foreground text-sm">No checklist items.</p>
            ) : (
              <ul className="divide-y">
                {ob.items.map((item) => (
                  <li key={item.id} className="flex items-center justify-between gap-3 py-2.5">
                    <span
                      className={
                        item.is_done ? 'text-muted-foreground line-through' : 'text-foreground'
                      }
                    >
                      {item.title}
                    </span>
                    <Switch
                      checked={item.is_done}
                      disabled={busy}
                      onCheckedChange={(checked) => onToggle(item.id, checked)}
                      aria-label={`Mark "${item.title}" ${item.is_done ? 'not done' : 'done'}`}
                    />
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function OnboardingGroupedByStatus({ rows, onToggle, busy }: CardsProps) {
  const groups = useMemo(
    () =>
      ONBOARDING_STATUSES.map((s) => ({
        key: s.value,
        label: s.label,
        bucket: rows.filter((r) => r.status === s.value),
      })).filter((g) => g.bucket.length > 0),
    [rows],
  );
  if (rows.length === 0) return <EmptyText />;
  return (
    <div className="space-y-6">
      {groups.map(({ key, label, bucket }) => (
        <section key={key}>
          <h2 className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase">
            {label}
            <Badge variant="outline" className="text-[10px]">
              {bucket.length}
            </Badge>
          </h2>
          <OnboardingCards rows={bucket} onToggle={onToggle} busy={busy} />
        </section>
      ))}
    </div>
  );
}

function EmptyText() {
  return <CabinetEmptyState>You have no onboarding checklists.</CabinetEmptyState>;
}
